import type { DistributiveOmit } from '@tanstack/react-query'
import { match } from 'ts-pattern'
import type { AreEquivalent } from '../types.ts'
import {
  CreateRepositoryIntegrationRequestSchema,
  RepositoryTypeSchema,
  type CreateRepositoryIntegrationRequest,
  type Integration,
  type Repository,
  type RepositoryIntegration,
  type RepositoryType,
  type ScanResponse,
  type Tool,
} from './api-client/api-schemas'

export type ToolIntegration = Extract<
  Integration,
  { type: 'sonar' | 'checkmarx' | 'appscan' | 'veracode' | 'arnica' | 'datadog' | 'polaris' }
>

/**
 * The tool types a user can map to a repository *by hand*.
 *
 * Polaris is excluded, but not because it has no repository mapping — the platform models one and
 * creates it automatically, harvesting the project id from the SARIF because, in the backend's
 * words, "the caller doesn't know the Polaris project ID". There is therefore nothing for a user to
 * enter, which is what makes it unmappable here rather than unmapped everywhere.
 */
export type MappableIntegrationType = Exclude<ToolIntegration['type'], 'polaris'>

// Derived from the request union, so a variant shipping upstream widens the drawer with no second
// list to maintain. Only the deliberate Polaris exclusion is written down.
export const TOOL_INTEGRATION_TYPES: readonly ToolIntegration['type'][] =
  CreateRepositoryIntegrationRequestSchema.options.map(option => option.shape.type.value)

export const MAPPABLE_INTEGRATION_TYPES: readonly MappableIntegrationType[] = TOOL_INTEGRATION_TYPES.filter(
  (type): type is MappableIntegrationType => type !== 'polaris'
)

// The union is the authority on what may be mapped; this fails the build if a type it gains is not
// reflected in `MappableIntegrationType`, rather than silently dropping out of the drawer.
true satisfies AreEquivalent<MappableIntegrationType, Exclude<CreateRepositoryIntegrationRequest['type'], 'polaris'>>

/** A tool integration a user can map by hand — every `ToolIntegration` except Polaris. */
export type MappableIntegration = Extract<Integration, { type: MappableIntegrationType }>

const MAPPABLE_INTEGRATION_TYPE_SET = new Set<string>(MAPPABLE_INTEGRATION_TYPES)

/**
 * Distinct from `isToolIntegration`: that one answers "does this supply findings", which since
 * Polaris joined is no longer the same question as "can this be mapped here". Gating a Map-new
 * affordance on the wider test offers a picker with no options for an org whose only tool is Polaris.
 */
export const isMappableIntegration = (integration: Integration): integration is MappableIntegration =>
  MAPPABLE_INTEGRATION_TYPE_SET.has(integration.type)

/**
 * The integration types that are source providers rather than finding producers. Derived from
 * `RepositoryTypeSchema` minus the bare `git` case, which is a repository kind with no integration.
 */
export type ScmIntegrationType = Exclude<RepositoryType, 'git'>

export const SCM_INTEGRATION_TYPES = RepositoryTypeSchema.options.filter(
  (type): type is ScmIntegrationType => type !== 'git'
)

const SCM_INTEGRATION_TYPE_SET = new Set<string>(SCM_INTEGRATION_TYPES)

export const getIsScmIntegrationType = (type: Integration['type']): type is ScmIntegrationType =>
  SCM_INTEGRATION_TYPE_SET.has(type)

const TOOL_INTEGRATION_TYPE_SET = new Set<string>(TOOL_INTEGRATION_TYPES)

export const isToolIntegration = (integration: Integration): integration is ToolIntegration =>
  TOOL_INTEGRATION_TYPE_SET.has(integration.type)

/**
 * The integration type that owns a tool's findings, or undefined for a tool with no integration of
 * its own (an uploaded SARIF, or a tool Pixee reads through another provider).
 */
export function convertToolToIntegrationType(tool: Tool): ToolIntegration['type'] | undefined {
  return match(tool)
    .with('arnica_sast', () => 'arnica' as const)
    .with('datadog_sast', () => 'datadog' as const)
    .with('polaris_sast', () => 'polaris' as const)
    .with('sonar', 'checkmarx', 'appscan', 'veracode', t => t)
    .otherwise(() => undefined)
}

export function convertIntegrationTypeToTool(type: RepositoryIntegration['type']): Tool {
  return match(type)
    .with('arnica', () => 'arnica_sast' as const)
    .with('datadog', () => 'datadog_sast' as const)
    .with('polaris', () => 'polaris_sast' as const)
    .otherwise(t => t)
}

/**
 * Names an integration *type*. Split out of `getIntegrationName` so a surface holding only the type
 * — a tool filter, a picker's type control — reads the same table as one holding the integration,
 * instead of routing through the long-form tool names and calling Polaris something else.
 */
export function getIntegrationTypeName(type: Integration['type']): string {
  return match(type)
    .with('polaris', () => 'Polaris')
    .with('sonar', () => 'SonarQube')
    .with('checkmarx', () => 'Checkmarx')
    .with('veracode', () => 'Veracode')
    .with('appscan', () => 'AppScan')
    .with('arnica', () => 'Arnica')
    .with('datadog', () => 'Datadog')
    .otherwise(other => (other.length > 0 ? other.charAt(0).toUpperCase() + other.slice(1) : 'external tool'))
}

export function getIntegrationName(integration: Integration | null | undefined): string | undefined {
  return integration ? getIntegrationTypeName(integration.type) : undefined
}

/**
 * The producing integration instance's name. `_embedded.integration` is omitted for disconnected
 * scans, so callers render nothing rather than an empty label.
 */
export function getScanInstanceName(scan: Pick<ScanResponse, '_embedded'>): string | undefined {
  return scan._embedded?.integration?.name
}

/**
 * What a tool calls the identifier tying one of its projects to a repository, or `undefined` for a
 * tool whose mapping carries none. Shared because the mapping form asks for it and the removal
 * warning names what is discarded.
 *
 * Undefined rather than a default on purpose: a `'Project key'` fallback makes every tool the union
 * gains claim a Sonar-shaped identifier, which is how the removal warning once told someone
 * unmapping Polaris that a key they never entered would be asked for again.
 */
export function getRepositoryIdentifierLabel(tool: Tool | undefined): string | undefined {
  if (tool === undefined) {
    return undefined
  }
  return match(tool)
    .with('sonar', () => 'Project key')
    .with('checkmarx', () => 'Project ID')
    .with('appscan', () => 'Application ID')
    .with('veracode', () => 'Application GUID')
    .with('arnica_sast', () => 'Asset key')
    .with('datadog_sast', () => 'Repository ID')
    .otherwise(() => undefined)
}

// Only the first character is lowered: every label but "Project key" leads with an acronym, which
// `toLowerCase()` would mangle.
export function getRepositoryIdentifierPlaceholder(tool: Tool | undefined): string | undefined {
  const label = getRepositoryIdentifierLabel(tool)
  return label === undefined ? undefined : `Enter ${label.charAt(0).toLowerCase()}${label.slice(1)}`
}

export function getToolName(tool: undefined): undefined
export function getToolName(tool: Tool): string
export function getToolName(tool: Tool | undefined): string | undefined
export function getToolName(tool: Tool | undefined) {
  if (!tool) {
    return undefined
  }

  return match(tool)
    .with('aikido_sast', () => 'Aikido SAST')
    .with('aikido_sca', () => 'Aikido SCA')
    .with('appscan', () => 'AppScan')
    .with('polaris_sast', () => 'Black Duck Polaris SAST')
    .with('polaris_sca', () => 'Black Duck Polaris SCA')
    .with('checkmarx', () => 'Checkmarx')
    .with('codeql', () => 'CodeQL')
    .with('contrast', () => 'Contrast')
    .with('defectdojo', () => 'DefectDojo')
    .with('fortify', () => 'Fortify')
    .with('gitlab_sast', () => 'GitLab SAST')
    .with('gitlab_dependency_scanning', () => 'GitLab SCA')
    .with('dependabot', () => 'Dependabot')
    .with('semgrep', () => 'Semgrep')
    .with('snyk', () => 'Snyk')
    .with('sonar', () => 'SonarQube')
    .with('veracode', () => 'Veracode')
    .with('datadog_sast', () => 'Datadog')
    .with('arnica_sast', () => 'Arnica')
    .with('gitleaks', () => 'Gitleaks')
    .with('zap', () => 'OWASP ZAP')
    .exhaustive()
}

/**
 * Returns the repository name from a GitHub repository name in format "org/repo"
 * @param gitHubRepositoryNameIncludingOrg - The GitHub repository name in format "org/repo"
 * @returns The repository name
 */
function getGitHubRepositoryName(gitHubRepositoryNameIncludingOrg: string): string {
  const [_, repo] = gitHubRepositoryNameIncludingOrg.split('/')

  if (!repo) {
    console.warn(`Failed to parse repository name from ${gitHubRepositoryNameIncludingOrg}`)
    return gitHubRepositoryNameIncludingOrg
  }

  return repo
}

/**
 * Returns the organization name from a GitHub repository name in format "org/repo"
 * @param gitHubRepositoryNameIncludingOrg - The GitHub repository name in format "org/repo"
 * @returns The organization name
 */
function getGitHubOrganizationName(gitHubRepositoryNameIncludingOrg: string): string {
  const [org] = gitHubRepositoryNameIncludingOrg.split('/')

  if (!org) {
    console.warn(`Failed to parse organization name from ${gitHubRepositoryNameIncludingOrg}`)
    return gitHubRepositoryNameIncludingOrg
  }

  return org
}

export const getRepositoryTypeDisplayName = (type: Repository['type']) =>
  match(type)
    .with('azure', () => 'Azure')
    .with('github', () => 'GitHub')
    .with('gitlab', () => 'GitLab')
    .with('bitbucket', () => 'Bitbucket')
    .with('git', () => 'Git')
    .exhaustive()

export const getRepositoryOrganization = (repository: DistributiveOmit<Repository, '_links'>) =>
  match(repository)
    .with({ type: 'azure' }, repo => repo.organization)
    .with({ type: 'gitlab' }, repo => repo.namespace)
    .with({ type: 'github' }, repo => getGitHubOrganizationName(repo.full_name))
    .with({ type: 'bitbucket' }, repo => repo.workspace)
    .with({ type: 'git' }, () => 'no-organization')
    .exhaustive()

export const getRepositoryName = (repository: DistributiveOmit<Repository, '_links'>) =>
  match(repository)
    .with({ type: 'azure' }, repo => repo.project)
    .with({ type: 'github' }, repo => getGitHubRepositoryName(repo.full_name))
    .with({ type: 'gitlab' }, repo => repo.name)
    .with({ type: 'bitbucket' }, repo => repo.name)
    .with({ type: 'git' }, repo => repo.name)
    .exhaustive()
